'use client';

import Link from 'next/link';
import React, { useCallback, useMemo, useRef } from 'react';

import { LinearProgress } from '../../atomic-atoms';
import { Tooltip } from '../../atomic-atoms/Tooltip';

import styles from './PageTabs.module.scss';

export type TabItem = {
  label: string;
  href: string;
  disabled?: boolean;
  isLoading?: boolean;
  tooltip?: string;
};

export type PageTabsProps = {
  tabs: TabItem[];
  activeHref: string;
  wrap?: boolean;
  className?: string;
  tabClassName?: string;
  orientation?: 'horizontal' | 'vertical';
  'aria-label'?: string;
};

export function PageTabs({
  tabs,
  activeHref,
  wrap = false,
  className,
  tabClassName,
  orientation = 'horizontal',
  'aria-label': ariaLabel,
}: PageTabsProps) {
  const tabRefs = useRef<Map<string, HTMLAnchorElement>>(new Map());

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent, currentIndex: number) => {
      let newIndex = currentIndex;

      if (orientation === 'horizontal') {
        if (event.key === 'ArrowLeft') {
          newIndex = Math.max(0, currentIndex - 1);
        } else if (event.key === 'ArrowRight') {
          newIndex = Math.min(tabs.length - 1, currentIndex + 1);
        }
      } else {
        if (event.key === 'ArrowUp') {
          newIndex = Math.max(0, currentIndex - 1);
        } else if (event.key === 'ArrowDown') {
          newIndex = Math.min(tabs.length - 1, currentIndex + 1);
        }
      }

      if (event.key === 'Home') {
        newIndex = 0;
      } else if (event.key === 'End') {
        newIndex = tabs.length - 1;
      }

      if (newIndex !== currentIndex) {
        event.preventDefault();
        const newTab = tabs[newIndex];
        if (newTab) {
          tabRefs.current.get(newTab.href)?.focus();
        }
      }
    },
    [tabs, orientation],
  );

  const tabLinks = useMemo(() => {
    return tabs.map((tab, index) => {
      const isActive = activeHref === tab.href;
      const isDisabled = tab.disabled || tab.isLoading;

      const linkNode = (
        <Link
          key={tab.href}
          href={isDisabled ? '#' : tab.href}
          aria-label={tab.label}
          aria-selected={isActive}
          aria-disabled={isDisabled}
          role="tab"
          id={`page-tab-${index}`}
          tabIndex={isActive ? 0 : -1}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (isDisabled) {
              e.preventDefault();
              return;
            }
            handleKeyDown(e, index);
          }}
          onClick={(e: React.MouseEvent) => {
            if (isDisabled) {
              e.preventDefault();
            }
          }}
          ref={(el: HTMLAnchorElement | null) => {
            if (el) {
              tabRefs.current.set(tab.href, el);
            } else {
              tabRefs.current.delete(tab.href);
            }
          }}
          className={`${styles.tabLink} ${tabClassName || ''}`}
          data-variant={isActive ? 'primary' : undefined}
          data-loading={tab.isLoading ? 'true' : undefined}
        >
          {tab.isLoading ? (
            <LinearProgress>{tab.label}</LinearProgress>
          ) : (
            tab.label
          )}
        </Link>
      );

      if (tab.tooltip) {
        return (
          <Tooltip key={tab.href} title={tab.tooltip}>
            {linkNode}
          </Tooltip>
        );
      }

      return linkNode;
    });
  }, [tabs, activeHref, tabClassName, handleKeyDown]);

  if (tabs.length === 0) {
    return null;
  }

  return (
    <div className={`${styles.pageTabs} ${className || ''}`}>
      <div
        role="tablist"
        aria-label={ariaLabel || 'Page tabs'}
        className={`${styles.tabsContainer} ${
          orientation === 'vertical' ? styles.tabsContainerVertical : ''
        } ${wrap ? styles.tabsContainerWrap : ''}`}
      >
        {tabLinks}
      </div>
    </div>
  );
}

// * Usage Examples:
// * Example 1: Basic page tabs with routing
// export function BasicPageTabsExample() {
//   const pathname = '/me/my-tasks';

//   const tabs: TabItem[] = [
//     { label: 'My Tasks', href: '/me/my-tasks', isLoading: false },
//     { label: 'My Lists', href: '/me/my-lists' },
//     { label: 'My Time', href: '/me/my-time' },
//     { label: 'My Performance', href: '/me/my-performance' },
//   ];

//   return <PageTabs tabs={tabs} activeHref={pathname} />;
// }

// * Example 2: Page tabs with loading states
// export function PageTabsWithLoadingExample() {
//   const pathname = usePathname();

//   const tabs: TabItem[] = [
//     {
//       label: 'My Tasks',
//       href: '/me/my-tasks',
//       isLoading: false,
//     },
//     {
//       label: 'My Lists',
//       href: '/me/my-lists',
//       isLoading: true,
//     },
//   ];

//   return <PageTabs tabs={tabs} activeHref={pathname} />;
// }

// * Example 3: Disabled tabs
// export function DisabledPageTabsExample() {
//   const pathname = usePathname();

//   const tabs: TabItem[] = [
//     { label: 'My Tasks', href: '/me/my-tasks' },
//     { label: 'My Lists', href: '/me/my-lists', disabled: true },
//     { label: 'My Time', href: '/me/my-time' },
//   ];

//   return <PageTabs tabs={tabs} activeHref={pathname} />;
// }

// * Example 4: Complete layout example
// export function LayoutExample() {
//   const pathname = usePathname();

//   const tabs: TabItem[] = [
//     { label: 'My Tasks', href: '/me/my-tasks' },
//     { label: 'My Lists', href: '/me/my-lists' },
//     { label: 'My Time', href: '/me/my-time' },
//     { label: 'My Performance', href: '/me/my-performance' },
//   ];

//   return (
//     <div>
//       <PageTabs tabs={tabs} activeHref={pathname} />
//       {/* Page content is rendered by Next.js routing, not by PageTabs */}
//     </div>
//   );
// }
